import { ref, get, update, query, orderByKey, limitToFirst } from 'firebase/database';
import { getStorage, ref as storageRef, getDownloadURL, getMetadata, uploadBytes } from 'firebase/storage';
import { ZipWriter, ZipReader, BlobReader, BlobWriter, TextReader, TextWriter, Entry } from '@zip.js/zip.js';
import { app, auth, db } from '../firebase';
import { formatDateForStorage } from '../Helpers/DateUtils';
import { generateSlug } from '../Helpers/slugUtils';
import { processInBatches, retryWithBackoff } from '../Helpers/UploadUtils';
import { getAppVersion } from '../utils/version';
import { recordActivity } from './activityApi';
import {
    BUNDLE_PATHS,
    EXPORT_SCHEMA_VERSION,
    BuildingExportManifest,
    ExportedUsers,
} from '../utils/buildingTransfer/bundleFormat';
import {
    collectExportBinaryPaths,
    collectReferencedUids,
    countComponents,
    projectUsersNameOnly,
    sanitizeBuildingForExport,
    sanitizeSummaryForExport,
    validateBuildingKey,
} from '../utils/buildingTransfer/subtreeWalk';
import { applyUserResolution } from '../utils/buildingTransfer/applyUserResolution';
import { sniffContentType } from '../utils/buildingTransfer/contentSniff';
import {
    BuildingImportPreview,
    bundleEntryToStoragePath,
    buildImportPreview,
    findDrawingsWithoutSource,
    buildSummaryUpdates,
    collectConfigReferences,
    parseManifest,
} from '../utils/buildingTransfer/importLogic';

/**
 * API for exporting one building — inventory, conditions, photos, documents —
 * to a ZIP file and importing such a file into another instance
 * (typically prod → test/demo). Superadmin only; plan in
 * docs/plans/building-export-import.md.
 *
 * The ZIP is streamed straight to disk via the File System Access API
 * (Chrome/Edge), so memory stays flat regardless of building size. Thumbnail
 * derivatives are not bundled — the target's thumbnail trigger regenerates
 * them, since the import writes DB metadata before uploading bytes.
 */

/** Minimal File System Access API surface (not yet in this project's TS DOM lib). */
interface SaveFilePickerWindow {
    showSaveFilePicker?: (options?: {
        suggestedName?: string;
        types?: Array<{ description?: string; accept: Record<string, string[]> }>;
    }) => Promise<{ createWritable: () => Promise<WritableStream & { abort?: () => Promise<void> }> }>;
}

export const isDirectSaveSupported = (): boolean =>
    typeof (window as SaveFilePickerWindow).showSaveFilePicker === 'function';

export interface ExportProgress {
    phase: 'preparing' | 'zipping' | 'finishing';
    filesTotal: number;
    filesDone: number;
    bytesDone: number;
    currentFile?: string;
}

export interface BuildingExportResult {
    manifest: BuildingExportManifest;
    warnings: string[];
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    !!value && typeof value === 'object' && !Array.isArray(value);

const isObjectNotFound = (err: unknown): boolean =>
    isRecord(err) && (err as { code?: unknown }).code === 'storage/object-not-found';

/**
 * Export one building to a ZIP chosen via the save-file dialog. Must be called
 * from a user gesture (the dialog opens before any slow reads). `buildingName`
 * comes from the already-loaded summary list and only seeds the filename.
 */
export const exportBuildingToZip = async (
    rawBuildingKey: string,
    buildingName: string,
    onProgress: (progress: ExportProgress) => void
): Promise<BuildingExportResult> => {
    const buildingKey = validateBuildingKey(rawBuildingKey);
    const operatorUid = auth.currentUser?.uid;
    if (!operatorUid) {
        throw new Error('Du måste vara inloggad');
    }

    const picker = (window as SaveFilePickerWindow).showSaveFilePicker;
    if (!picker) {
        throw new Error(
            'Exporten kräver en webbläsare med stöd för att spara stora filer direkt till disk (Chrome eller Edge)'
        );
    }

    const date = formatDateForStorage(new Date()).slice(0, 10);
    // Open the dialog first — the user gesture that authorizes it expires quickly.
    const fileHandle = await picker({
        suggestedName: `mf-byggnad-${generateSlug(buildingName) || buildingKey}-${date}.zip`,
        types: [{ description: 'ZIP-arkiv', accept: { 'application/zip': ['.zip'] } }],
    });
    const writable = await fileHandle.createWritable();

    try {
        const zipWriter = new ZipWriter(writable);
        const progress: ExportProgress = { phase: 'preparing', filesTotal: 0, filesDone: 0, bytesDone: 0 };
        onProgress({ ...progress });

        const [buildingSnap, summarySnap] = await Promise.all([
            get(ref(db, `buildings/${buildingKey}`)),
            get(ref(db, `buildingsSummary/${buildingKey}`)),
        ]);
        if (!buildingSnap.exists()) {
            throw new Error('Byggnaden finns inte i databasen');
        }
        const building = sanitizeBuildingForExport(buildingSnap.val());
        const summary = summarySnap.exists() ? sanitizeSummaryForExport(summarySnap.val()) : null;

        const referencedUids = collectReferencedUids(building, summary);
        const allUsersSnap = await get(ref(db, 'users/userList'));
        const users: ExportedUsers = projectUsersNameOnly(
            allUsersSnap.exists() ? (allUsersSnap.val() as Record<string, { name?: string }>) : null,
            referencedUids
        );

        const warnings: string[] = [];
        const unnamedCount = referencedUids.length - Object.keys(users).length;
        if (unnamedCount > 0) {
            warnings.push(`${unnamedCount} refererade användare saknas i användarlistan och följer med utan namn`);
        }
        // The file list comes from the DB metadata, not a Storage list call —
        // the instances run Storage rules_version 1, where list is unsupported.
        const { included: binaryPaths, skipped: skippedStoragePaths } = collectExportBinaryPaths(
            building,
            summary,
            buildingKey
        );
        if (skippedStoragePaths.length > 0) {
            warnings.push(
                `${skippedStoragePaths.length} refererade filer ligger utanför byggnadens lagringsyta och följer inte med`
            );
        }

        await zipWriter.add(BUNDLE_PATHS.building, new TextReader(JSON.stringify(building)));
        await zipWriter.add(BUNDLE_PATHS.buildingsSummary, new TextReader(JSON.stringify(summary)));
        await zipWriter.add(BUNDLE_PATHS.users, new TextReader(JSON.stringify(users)));

        progress.phase = 'zipping';
        progress.filesTotal = binaryPaths.length;
        onProgress({ ...progress });

        const storage = getStorage(app);
        const missingFiles: string[] = [];
        // One file at a time: bounded memory, and the ZIP central directory
        // stays consistent. Images are already compressed — store, don't deflate.
        for (const binaryPath of binaryPaths) {
            progress.currentFile = binaryPath;
            onProgress({ ...progress });
            let url: string;
            try {
                url = await getDownloadURL(storageRef(storage, binaryPath));
            } catch (err) {
                if (isObjectNotFound(err)) {
                    // The DB references a file that no longer exists in Storage —
                    // same broken image in source and target; skip, don't fail.
                    missingFiles.push(binaryPath);
                    progress.filesDone++;
                    continue;
                }
                throw err;
            }
            const blob = await retryWithBackoff(async () => {
                const response = await fetch(url);
                if (!response.ok) {
                    throw new Error(`Nedladdning misslyckades (${response.status}) för ${binaryPath}`);
                }
                return response.blob();
            });
            await zipWriter.add(`${BUNDLE_PATHS.binariesDir}${binaryPath}`, new BlobReader(blob), {
                level: 0,
            });
            progress.filesDone++;
            progress.bytesDone += blob.size;
            onProgress({ ...progress });
        }
        if (missingFiles.length > 0) {
            warnings.push(
                `${missingFiles.length} refererade filer saknas i källinstansens lagring och följer inte med`
            );
        }

        let instanceName: string | undefined;
        try {
            const nameSnap = await get(ref(db, 'global/instanceName'));
            if (nameSnap.exists() && typeof nameSnap.val() === 'string') {
                instanceName = nameSnap.val();
            }
        } catch {
            // Metadata only — export works without it
        }

        const manifest: BuildingExportManifest = {
            exportSchemaVersion: EXPORT_SCHEMA_VERSION,
            appVersion: getAppVersion().version,
            exportedFrom: { projectId: app.options.projectId ?? '', instanceName },
            exportedAt: formatDateForStorage(new Date()),
            exportedBy: operatorUid,
            buildingKey,
            buildingName,
            counts: {
                components: countComponents(building),
                binaries: binaryPaths.length - missingFiles.length,
                totalBytes: progress.bytesDone,
                referencedUsers: referencedUids.length,
            },
            referencedUsers: Object.entries(users).map(([uid, user]) => ({ uid, name: user.name })),
            ...(skippedStoragePaths.length > 0 ? { skippedStoragePaths } : {}),
        };
        // Written last so the counts are accurate; entry order in the ZIP is irrelevant.
        await zipWriter.add(BUNDLE_PATHS.manifest, new TextReader(JSON.stringify(manifest, null, 2)));

        progress.phase = 'finishing';
        progress.currentFile = undefined;
        onProgress({ ...progress });
        await zipWriter.close();
        return { manifest, warnings };
    } catch (err) {
        try {
            await writable.abort?.();
        } catch {
            // The partial file could not be discarded — the thrown error below matters more
        }
        throw err;
    }
};

export interface BundleBinaryEntry {
    /** Destination Storage path, already confined to buildings/{key}/. */
    storagePath: string;
    getBlob: () => Promise<Blob>;
}

export interface ParsedBuildingBundle {
    manifest: BuildingExportManifest;
    building: unknown;
    summary: unknown;
    users: ExportedUsers;
    binaries: BundleBinaryEntry[];
    /** ZIP entries under binaries/ that failed path confinement — a crafted or corrupt file. */
    rejectedEntries: number;
    close: () => Promise<void>;
}

const CONTENT_TYPES: Record<string, string> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    heic: 'image/heic',
    heif: 'image/heif',
    pdf: 'application/pdf',
    mp4: 'video/mp4',
    mov: 'video/quicktime',
    txt: 'text/plain',
};

const contentTypeForPath = (path: string): string => {
    const extension = path.split('.').pop()?.toLowerCase() ?? '';
    return CONTENT_TYPES[extension] ?? 'application/octet-stream';
};

/**
 * The real content type of a bundled file: sniffed from its magic bytes
 * (photo paths have no extension), extension as fallback. Getting this right
 * is load-bearing — generateThumbnails skips any object whose contentType
 * does not start with "image/".
 */
const detectContentType = async (blob: Blob, storagePath: string): Promise<string> => {
    const bytes = new Uint8Array(await blob.slice(0, 16).arrayBuffer());
    return sniffContentType(bytes) ?? contentTypeForPath(storagePath);
};

const readEntryText = async (entry: Entry | undefined, label: string): Promise<string> => {
    if (!entry || entry.directory) {
        throw new Error(`Filen saknar ${label} och är inte en giltig byggnadsexport`);
    }
    return entry.getData(new TextWriter());
};

const parseEntryJson = (text: string, label: string): unknown => {
    try {
        return JSON.parse(text);
    } catch {
        throw new Error(`Filens ${label} kunde inte läsas som JSON`);
    }
};

/**
 * Open and validate an export ZIP. Binaries are read lazily entry by entry —
 * the file is never fully buffered. Call `close()` when done (after import or
 * when the user abandons the preview).
 */
export const parseBuildingBundle = async (file: File): Promise<ParsedBuildingBundle> => {
    const zipReader = new ZipReader(new BlobReader(file));
    let entries: Entry[];
    try {
        entries = await zipReader.getEntries();
    } catch {
        await zipReader.close();
        throw new Error('Filen kunde inte läsas som ett ZIP-arkiv');
    }

    try {
        const byName = new Map(entries.map(entry => [entry.filename, entry]));
        const manifest = parseManifest(await readEntryText(byName.get(BUNDLE_PATHS.manifest), 'manifest'));
        const buildingKey = validateBuildingKey(manifest.buildingKey);

        const building = parseEntryJson(
            await readEntryText(byName.get(BUNDLE_PATHS.building), 'byggnadsdata'),
            'byggnadsdata'
        );
        if (!isRecord(building)) {
            throw new Error('Filens byggnadsdata är tom eller ogiltig');
        }
        const summary = parseEntryJson(
            await readEntryText(byName.get(BUNDLE_PATHS.buildingsSummary), 'sammanfattning'),
            'sammanfattning'
        );
        const usersRaw = parseEntryJson(
            await readEntryText(byName.get(BUNDLE_PATHS.users), 'användarlista'),
            'användarlista'
        );
        const users: ExportedUsers = isRecord(usersRaw) ? (usersRaw as ExportedUsers) : {};

        const binaries: BundleBinaryEntry[] = [];
        let rejectedEntries = 0;
        for (const entry of entries) {
            if (!entry.filename.startsWith(BUNDLE_PATHS.binariesDir) || entry.directory) continue;
            const storagePath = bundleEntryToStoragePath(entry.filename, buildingKey);
            if (!storagePath) {
                rejectedEntries++;
                continue;
            }
            const fileEntry = entry;
            binaries.push({
                storagePath,
                getBlob: () => fileEntry.getData(new BlobWriter(contentTypeForPath(storagePath))),
            });
        }

        return {
            manifest,
            building,
            summary,
            users,
            binaries,
            rejectedEntries,
            close: () => zipReader.close(),
        };
    } catch (err) {
        await zipReader.close();
        throw err;
    }
};

/** Existence probe that avoids downloading the (possibly large) building node. */
const buildingExistsInTarget = async (buildingKey: string): Promise<boolean> => {
    const snap = await get(query(ref(db, `buildings/${buildingKey}`), orderByKey(), limitToFirst(1)));
    return snap.exists();
};

const readCatalogKeys = async (path: string): Promise<Set<string>> => {
    try {
        const snap = await get(ref(db, path));
        return snap.exists() && isRecord(snap.val())
            ? new Set(Object.keys(snap.val() as Record<string, unknown>))
            : new Set();
    } catch {
        return new Set();
    }
};

export const getBuildingImportPreview = async (
    bundle: ParsedBuildingBundle
): Promise<BuildingImportPreview> => {
    const { manifest, building, summary, users } = bundle;
    assertDrawingsHaveSource(building);

    const [buildingExists, componentTypeCatalog, materialCatalog, tagCatalog] = await Promise.all([
        buildingExistsInTarget(manifest.buildingKey),
        readCatalogKeys('config/buildComponentTypes'),
        readCatalogKeys('config/materials'),
        readCatalogKeys('buildingTags'),
    ]);

    const referencedUids = collectReferencedUids(building, summary);
    const uidExistence = await Promise.all(
        referencedUids.map(async uid => {
            // The whole node, not `/key`: a deleted user's tombstone has no `key` but still resolves.
            const snap = await get(ref(db, `users/userList/${uid}`));
            return [uid, snap.exists()] as const;
        })
    );
    const existingTargetUids = new Set(uidExistence.filter(([, exists]) => exists).map(([uid]) => uid));

    // Users referenced in the data but absent from users.json still belong in the
    // preview — shown without a name.
    const previewUsers: ExportedUsers = { ...users };
    for (const uid of referencedUids) {
        if (!previewUsers[uid]) previewUsers[uid] = { name: '' };
    }

    const references = collectConfigReferences(building, summary);
    return buildImportPreview(
        manifest,
        previewUsers,
        getAppVersion().version,
        buildingExists,
        existingTargetUids,
        {
            componentTypeKeys: references.componentTypeKeys.filter(key => !componentTypeCatalog.has(key)),
            materialKeys: references.materialKeys.filter(key => !materialCatalog.has(key)),
            tagKeys: references.tagKeys.filter(key => !tagCatalog.has(key)),
        }
    );
};

/**
 * The database rules require a PDF `source` on every drawing node, so a bundle
 * with a sourceless drawing would fail the atomic building write with a
 * permission error. Refuse it up front with a readable message instead.
 */
const assertDrawingsHaveSource = (building: unknown): void => {
    const missing = findDrawingsWithoutSource(building);
    if (missing.length === 0) return;
    throw new Error(
        `${missing.length} ritningar i filen saknar vektorkälla (PDF) och kan inte importeras. ` +
            'Exportera byggnaden på nytt från en instans där ritningarna har vektorkälla.'
    );
};

export interface ImportProgress {
    phase: 'database' | 'uploading';
    filesTotal: number;
    filesDone: number;
    currentFile?: string;
}

export interface BuildingImportResult {
    uploadedFiles: number;
    /** Files already present in the target bucket — Storage rules deny updates,
     * so a re-import keeps the existing (identical-source) object. */
    existingFiles: number;
    failedFiles: Array<{ storagePath: string; message: string }>;
}

export interface BuildingImportOptions {
    /** Rewrite every UID-bearing field to the importing superadmin's UID. */
    remapAllToMe: boolean;
}

/**
 * Write the building into this instance: one atomic multi-path DB update
 * (whole-node building replace + per-field summary update), then the binaries.
 * DB before bytes — the thumbnail trigger and iOS both rely on metadata
 * existing when a file lands.
 */
export const runBuildingImport = async (
    bundle: ParsedBuildingBundle,
    options: BuildingImportOptions,
    onProgress: (progress: ImportProgress) => void
): Promise<BuildingImportResult> => {
    const operatorUid = auth.currentUser?.uid;
    if (!operatorUid) {
        throw new Error('Du måste vara inloggad');
    }
    const buildingKey = validateBuildingKey(bundle.manifest.buildingKey);
    assertDrawingsHaveSource(bundle.building);

    let resolvedBuilding = bundle.building;
    let resolvedSummary = bundle.summary;
    if (options.remapAllToMe) {
        const resolution = Object.fromEntries(
            collectReferencedUids(bundle.building, bundle.summary)
                .filter(uid => uid !== operatorUid)
                .map(uid => [uid, { strategy: 'remap' as const, targetUid: operatorUid }])
        );
        const resolved = applyUserResolution(bundle.building, bundle.summary, bundle.users, resolution, {
            operatorUid,
            now: formatDateForStorage(new Date()),
            existingTargetUids: new Set([operatorUid]),
            activeTargetUids: new Set([operatorUid]),
        });
        resolvedBuilding = resolved.building;
        resolvedSummary = resolved.summary;
    }

    onProgress({ phase: 'database', filesTotal: bundle.binaries.length, filesDone: 0 });
    const existingSummarySnap = await get(ref(db, `buildingsSummary/${buildingKey}`));
    const updates: Record<string, unknown> = {
        [`buildings/${buildingKey}`]: resolvedBuilding,
        ...buildSummaryUpdates(
            buildingKey,
            resolvedSummary,
            existingSummarySnap.exists() ? existingSummarySnap.val() : null
        ),
    };
    await update(ref(db), updates);
    const importedName = (resolvedSummary as { name?: unknown } | null | undefined)?.name;
    recordActivity('building.imported', { buildingKey, targetKey: buildingKey, label: typeof importedName === 'string' ? importedName : undefined });

    const storage = getStorage(app);
    const failedFiles: BuildingImportResult['failedFiles'] = [];
    let existingFiles = 0;
    let filesDone = 0;

    await processInBatches(
        bundle.binaries,
        async binary => {
            const fileRef = storageRef(storage, binary.storagePath);
            try {
                // Storage rules deny updates, so an object that already exists
                // (a re-import over the same building) cannot and need not be
                // replaced — its content came from the same source path.
                const existingMetadata = await getMetadata(fileRef).catch(err => {
                    if (isObjectNotFound(err)) return null;
                    throw err;
                });
                if (existingMetadata) {
                    const existingType = existingMetadata.contentType ?? '';
                    let brokenType = false;
                    if (!existingType || existingType === 'application/octet-stream') {
                        // Possibly a file from an import made before content sniffing
                        // existed — thumbnails never generate for it, and the rules
                        // block repairing the metadata from the client.
                        const realType = await detectContentType(await binary.getBlob(), binary.storagePath);
                        brokenType = realType.startsWith('image/');
                    }
                    if (brokenType) {
                        failedFiles.push({
                            storagePath: binary.storagePath,
                            message:
                                'Filen finns redan med fel filtyp (application/octet-stream) och kan inte skrivas över — ta bort byggnadens filer i lagringen och importera igen',
                        });
                    } else {
                        existingFiles++;
                    }
                } else {
                    const blob = await binary.getBlob();
                    const contentType = await detectContentType(blob, binary.storagePath);
                    await retryWithBackoff(() => uploadBytes(fileRef, blob, { contentType }));
                }
            } catch (err) {
                failedFiles.push({
                    storagePath: binary.storagePath,
                    message: err instanceof Error ? err.message : String(err),
                });
            }
            filesDone++;
            onProgress({
                phase: 'uploading',
                filesTotal: bundle.binaries.length,
                filesDone,
                currentFile: binary.storagePath,
            });
        },
        3
    );

    return {
        uploadedFiles: bundle.binaries.length - failedFiles.length - existingFiles,
        existingFiles,
        failedFiles,
    };
};
